/**
 * Decimals on the client, and the one boundary where they are allowed to become floats.
 *
 * H6: money and quantities are decimals, never IEEE doubles. The API honours that all the
 * way out — `DecimalStr` declares `string` in the schema and the generated models type every
 * price as `string` (`tests/test_web_api_contract.py` is the check). This module is where
 * that guarantee either survives the frontend or quietly dies in it.
 *
 * ## The rule
 *
 * **A price stays a `string` from the wire to the pixel, except inside a charting library.**
 *
 * TradingView Lightweight Charts takes `number` for its series data, and that is correct: it
 * is computing a y-coordinate, and a y-coordinate is a float by nature. Converting there
 * costs nothing that matters — the worst case is a candle drawn a fraction of a pixel off.
 *
 * Converting *anywhere else* costs something real. `Number("0.1") + Number("0.2")` is
 * `0.30000000000000004`, and a price the operator reads, copies into an order, or compares
 * against a limit must not have been through that. So:
 *
 * - **Display** — the `string` the API sent, formatted for grouping only. Never `Number()`.
 * - **Arithmetic** — `Decimal`, from `decimal.js`, which `Tooling-and-Libraries.md` marks
 *   **Required** for exactly this reason.
 * - **Plotting** — `toPlot()`, named so the conversion is visible in a diff, and used
 *   nowhere but where a chart library demands it.
 *
 * A grep for `Number(` or `parseFloat(` outside this file should find nothing.
 */

import Decimal from 'decimal.js';

/** Parse an API decimal string. Throws on anything that is not one, rather than `NaN`. */
export function dec(value: string): Decimal {
  const parsed = new Decimal(value);
  if (!parsed.isFinite()) {
    // `new Decimal("abc")` throws already; this catches `Infinity`, which does not, and
    // which would render as "Infinity" in a price column rather than as an error.
    throw new Error(`not a finite decimal: ${value}`);
  }
  return parsed;
}

/**
 * A decimal string as a `number`, **for a chart axis and nothing else**.
 *
 * Named for its one legitimate use so that the conversion is greppable and a reviewer can
 * see it happening. `Decimal.toNumber()` is the lossy step, taken deliberately and once.
 */
export function toPlot(value: string): number {
  return dec(value).toNumber();
}

/**
 * Format a decimal string for display, grouping thousands, without changing its value.
 *
 * Operates on the string rather than on a `number`: the API already sent the digits it means
 * — `DecimalStr` is non-exponential and keeps trailing zeros, so `"71204.30"` arrives with
 * its two decimal places intact and rounding here would throw away the precision the whole
 * chain exists to preserve.
 *
 * **Commas, matching the design system.** `Num`'s own `formatNum` groups with
 * `toLocaleString('en-GB')` and its documented output is `€12,431.02`. Two grouping
 * characters on one screen is worse than either one, and `Num` is the authority.
 *
 * The output is meant to be handed to `Num` as a **string**, which `formatNum` returns
 * untouched (`if (typeof value === 'string') return value`) — so the component supplies the
 * tabular figures, the slashed zero and the number ramp while the value stays exactly the
 * digits the API sent. Passing a `number` there instead would be the float H6 excludes.
 */
export function formatDecimal(value: string, places?: number): string {
  // Rounded in `Decimal`, never in a float. `toFixed` on a Number would be the H6 breach this
  // module exists to prevent, and it is the obvious thing to reach for — a backtest report is
  // the first screen with enough figures to want two decimal places everywhere.
  const source = places === undefined ? value : dec(value).toFixed(places);
  const negative = source.startsWith('-');
  const unsigned = negative ? source.slice(1) : source;
  const [whole = '0', fraction] = unsigned.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // U+2212 for the minus, matching `Num`, which uses it because it aligns with the digits
  // where a hyphen does not.
  return `${negative ? '\u2212' : ''}${grouped}${fraction ? `.${fraction}` : ''}`;
}

/**
 * A **price**, formatted so a micro-cap is still a number.
 *
 * `formatDecimal(value, 2)` is right for a euro amount and wrong for a price. `BABYDOGE/EUR`
 * closes at `0.000000000262` and two decimal places render that as **`0.00`** — in the Entry and
 * Exit columns of the trade list, and in the entry/exit legend of the per-trade chart. Roughly
 * seventy of this venue's seventy-seven tradeable pairs price at that end, which is the half of
 * the universe D24 points the platform at.
 *
 * This is the same class of defect as the one task 21 fixed on the wire, one layer up: `plain()`
 * made the *transport* exact and the display then rounded it away. **A rule about how a type is
 * written down has to live where the type does**, so it lives here rather than at each call site.
 *
 * At or above one, two decimal places — a price of 67,644.08 does not want four. Below one, the
 * interesting digits are the significant ones. Rounded in `Decimal`, and `toFixed()` with no
 * argument because `toString()` on a small `Decimal` returns exponential notation, which is the
 * exact bug being avoided.
 *
 * Found by audit, 2026-08-02.
 */
export function formatPrice(value: string, significant = 4): string {
  const amount = dec(value);
  if (amount.isZero() || amount.abs().gte(1)) return formatDecimal(value, 2);
  return formatDecimal(amount.toSignificantDigits(significant).toFixed());
}

/** The change between two prices, as a percentage string with a sign. Decimal throughout. */
export function changePct(from: string, to: string): string {
  const start = dec(from);
  if (start.isZero()) return '—';
  const pct = dec(to).minus(start).div(start).times(100);
  // Always signed, and U+2212 for the minus — `Num`'s rule for percentages, restated here
  // because this value is produced as a string and does not pass through its formatter.
  return pct.isNegative() ? `−${pct.abs().toFixed(2)}%` : `+${pct.toFixed(2)}%`;
}
